import axios from 'axios';
import authService from './authService';
import router from '@/router';
import { API_URL } from '@/config/api';

const api = axios.create({
    baseURL: API_URL,
    timeout: 10000,
});

api.interceptors.request.use(
    (config) => {
        const token = authService.token.get();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    (error) => {
        // Só faz logout se a rota atual requer autenticação
        if (error.response?.status === 401) {
            const currentRoute = router.currentRoute;
            const requiresAuth = currentRoute.matched.some(record => record.meta.requiresAuth === true);

            if (requiresAuth) {
                authService.logout();
            }
        }
        return Promise.reject(error);
    }
);

const TIPO_COMPROMISSO_OPTIONS = [
    { value: 'AUDIENCIA', text: 'Audiência' },
    { value: 'REUNIAO', text: 'Reunião' },
    { value: 'PRAZO_PROCESSUAL', text: 'Prazo Processual' },
    { value: 'CONSULTA', text: 'Consulta' },
    { value: 'DEPOIMENTO', text: 'Depoimento' },
    { value: 'VISITA', text: 'Visita' },
    { value: 'OUTRO', text: 'Outro' }
];

const formatarCompromissoParaExibicao = (compromisso) => {
    if (!compromisso) return null;

    const dataHora = new Date(compromisso.dataHora);

    return {
        id: compromisso.id,
        titulo: compromisso.titulo || '',
        descricao: compromisso.descricao || '',
        tipo: compromisso.tipo || '',
        data: dataHora.toLocaleDateString('pt-BR'),
        hora: dataHora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        local: compromisso.local || '',
        clienteId: compromisso.clienteId,
        clienteNome: compromisso.clienteNome || '',
        status: compromisso.status || 'AGENDADO'
    };
};

const formatarCompromissoParaAPI = (compromisso) => {
    let dataHora = null;
    if (compromisso.data && compromisso.hora) {
        try {
            let dataFormatada = compromisso.data;

            if (compromisso.data.includes('/')) {
                const [dia, mes, ano] = compromisso.data.split('/');
                dataFormatada = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
            }

            dataHora = `${dataFormatada}T${compromisso.hora}:00`;
        } catch (error) {
            console.error("Erro ao converter data e hora:", error);
            throw new Error("Data ou hora inválida");
        }
    }

    return {
        titulo: compromisso.titulo,
        descricao: compromisso.descricao,
        dataHora,
        local: compromisso.local,
        tipo: compromisso.tipo,
        status: compromisso.status,
        clienteId: compromisso.clienteId
    };
};

const compromissoService = {
    TIPO_COMPROMISSO_OPTIONS,

    async getCompromissos() {
        const response = await api.get('/compromissos');
        return response.data.map(formatarCompromissoParaExibicao);
    },

    async getCompromissoPorId(id) {
        const response = await api.get(`/compromissos/${id}`);
        return formatarCompromissoParaExibicao(response.data);
    },

    async getCompromissosPorCliente(clienteId) {
        const response = await api.get(`/compromissos/cliente/${clienteId}`);
        return response.data.map(formatarCompromissoParaExibicao);
    },

    async getCompromissosPorPeriodo(inicio, fim) {
        const response = await api.get('/compromissos/periodo', {
            params: { inicio, fim }
        });
        return response.data.map(formatarCompromissoParaExibicao);
    },

    async buscarCompromissosPorTitulo(titulo) {
        const response = await api.get('/compromissos/buscar', {
            params: { titulo }
        });
        return response.data.map(formatarCompromissoParaExibicao);
    },

    async criarCompromisso(compromissoData) {
        const formattedData = formatarCompromissoParaAPI(compromissoData);
        const response = await api.post('/compromissos', formattedData);
        return response.data;
    },

    async atualizarCompromisso(id, compromissoData) {
        const formattedData = formatarCompromissoParaAPI(compromissoData);
        await api.put(`/compromissos/${id}`, formattedData);
        return true;
    },

    async excluirCompromisso(id) {
        await api.delete(`/compromissos/${id}`);
        return true;
    },

    async getCompromissosProximosAVencer(diasProximosAVencer = 15) {
        try {
            const resultado = await api.get('/compromissos/status', { params: { diasProximosAVencer } });
            return {
                vencidos: resultado.data.vencidos.map(formatarCompromissoParaExibicao),
                proximosAVencer: resultado.data.proximosAVencer.map(formatarCompromissoParaExibicao)
            };
        } catch (error) {
            console.error('Erro ao buscar compromissos vencidos e próximos a vencer:', error);
            return { vencidos: [], proximosAVencer: [] };
        }
    }
};

export { compromissoService };